import { useMarket } from '@ecosystem/market-context-selection';
import { useAuthentication } from '@ecosystem/newron-client-lib';
import { useEffect, useMemo, useRef, useState } from 'react';

import { PageDetailsSelector, PageName, PageType, Product, Slide, SlidesByType } from '../common';
import { RetailMarkets, RetailSubscription, getRetailClientSubscription } from '../services/Retail.service';
import {
  UpsellMessageType,
  getPageBasedOnPath,
  getPageDetails,
  getUpsellCellSlides,
  getUpsellMessage,
  isTestUser,
} from '../utils';
import { getBannerMessages, getRLSBannerMessages } from '../utils/consumer-srories';

import { useRetailMarket } from './useRetailMarket';
import { useUpsellLocalStorage } from './useUpsellLocalStorage';

const pages = [
  PageType.BRAND,
  PageType.OVERVIEW,
  PageType.PRODUCTS,
  PageType.SEGMENTATION,
  PageType.OPPORTUNITY_REVIEW,
  PageType.PERFORMANCE_ANALYSIS,
  PageType.PRODUCT_ANALYZER,
];

type ReturnType = {
  showUpsell: boolean;
  slides: Slide[];
  slidesType?: SlidesByType;
  selectors: PageDetailsSelector[];
  pageName: PageName;
  UpsellMessage: UpsellMessageType;
  market?: string;
  bannerCount?: number;
};

export const useUpsellSlides: (products?: Product[]) => ReturnType = products => {
  const [isCampaignsDisabled, setIsCampaignsDisabled] = useState<boolean>(false);
  const [showUpsell, setShowUpsell] = useState<boolean>(false);
  const [slides, setSlides] = useState<Slide[]>([]);
  const [slidesType, setSlidesType] = useState<SlidesByType | undefined>(undefined);

  const [retailMarkets, setRIMarkets] = useState<RetailMarkets>({
    retailInsights: [],
    retailHitlistStandard: [],
  });
  const RetailSubscriptionsLoaded = useRef<boolean>(false);
  const { isAuthenticated, user, getAccessToken } = useAuthentication();
  const { market } = useMarket();
  const { market: retailMarket } = useRetailMarket(products);
  const { upsellShownByMarket, loading, onShowInCurrentSession } = useUpsellLocalStorage(products);

  const pathname = window.location.pathname;
  const pageType = useMemo(() => getPageBasedOnPath(pathname, products)?.type, [pathname, products]);

  const { itemGroupId, countryId } = useMemo(() => {
    let result;
    if (upsellShownByMarket?.market) {
      result = upsellShownByMarket?.market;
    }
    if (products.includes(Product.Retail)) {
      result = retailMarket;
    }

    if (products.includes(Product.Market)) {
      result = market;
    }

    return result ?? { itemGroupId: undefined, countryId: undefined };
  }, [market, products, retailMarket, upsellShownByMarket?.market]);

  const upsellCellSlides = useMemo(
    () => getUpsellCellSlides(itemGroupId, countryId, retailMarkets, products, user.clientId),
    [retailMarkets, products, countryId, itemGroupId, user.clientId]
  );

  const upsellMessage = useMemo(
    () => getUpsellMessage(products, upsellCellSlides.slidesType),
    [products, upsellCellSlides.slidesType]
  );

  const hasUpsellMessageContent = useMemo(() => {
    let result = true;

    if (upsellCellSlides.slidesType === SlidesByType.CONSUMER_LOST_SHOPPER) {
      result = !!getBannerMessages(upsellCellSlides.market, upsellShownByMarket.count);
    }

    if (upsellCellSlides.slidesType === SlidesByType.CONSUMER_RETAIL_LOST_SHOPPER) {
      result = !!getRLSBannerMessages(upsellCellSlides.market, upsellShownByMarket.count);
    }

    return result;
  }, [upsellCellSlides.market, upsellCellSlides.slidesType, upsellShownByMarket.count]);

  const skipForUsers = useMemo(() => {
    const { email } = user;
    if (!email) {
      return true;
    }
    return isTestUser(email);
  }, [user]);

  const pageDetails = useMemo(
    () => getPageDetails(pageType, upsellCellSlides.slidesType),
    [pageType, upsellCellSlides.slidesType]
  );

  useEffect(() => {
    const disableCookie = document.cookie
      .split('; ')
      .filter(row => row.startsWith('disable_campaigns='))
      .map(c => c.split('=')[1])[0];
    setIsCampaignsDisabled(!!disableCookie);
  }, []);

  useEffect(() => {
    if (
      isAuthenticated &&
      !isCampaignsDisabled &&
      products.includes(Product.Retail) &&
      !RetailSubscriptionsLoaded.current
    ) {
      (async () => {
        const token = await getAccessToken();
        const result = await getRetailClientSubscription(token);
        setRIMarkets({
          retailInsights: [
            ...new Set(result.retailInsights.map((sub: RetailSubscription) => `${sub.itemGroup ?? ''}/${sub.country}`)),
          ],
          retailHitlistStandard: [
            ...new Set(
              result.retailHitlistStandard.map((sub: RetailSubscription) => `${sub.itemGroup ?? ''}/${sub.country}`)
            ),
          ],
        });
        RetailSubscriptionsLoaded.current = true;
      })();
    }
    // eslint-disable-next-line
  }, [isAuthenticated, products, isCampaignsDisabled]);

  useEffect(() => {
    if (!isAuthenticated || isCampaignsDisabled) {
      return;
    }

    if (slides.length > 0) {
      setSlides([]);
      setSlidesType(undefined);
    }
    const wasShown = loading || upsellShownByMarket.shown;
    const applicableForUser = isAuthenticated && !skipForUsers;
    const hasContent = upsellCellSlides.slides.length > 0 && hasUpsellMessageContent;

    const show = Boolean(!wasShown && applicableForUser && hasContent && pages.includes(pageType));
    if (show) {
      onShowInCurrentSession();
      setSlides(upsellCellSlides.slides);
      setSlidesType(upsellCellSlides.slidesType);
    }
    setShowUpsell(show);
  }, [
    user.clientId,
    isAuthenticated,
    itemGroupId,
    countryId,
    pageType,
    upsellCellSlides,
    skipForUsers,
    upsellShownByMarket,
    loading,
    slides.length,
    hasUpsellMessageContent,
    retailMarkets,
    isCampaignsDisabled,
    onShowInCurrentSession,
    products,
  ]);

  return useMemo(
    () => ({
      showUpsell,
      market: upsellCellSlides.market ?? '',
      selectors: pageDetails.selectors,
      slides,
      slidesType,
      pageName: pageDetails.name,
      UpsellMessage: upsellMessage,
      bannerCount: upsellShownByMarket.count,
    }),
    [
      showUpsell,
      upsellCellSlides.market,
      pageDetails.selectors,
      pageDetails.name,
      slides,
      slidesType,
      upsellMessage,
      upsellShownByMarket.count,
    ]
  );
};
